import { inject, Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TorchCaptureCopy, TorchUnavailableReason } from '@jump-tech-frontend/media-capture';
import { PHOTO_SOURCE_CAMERA, PhotoSourceCopy, PhotoSourceModalComponent } from './modals/photo-source-modal.component';
import { TorchCaptureModalComponent } from './modals/torch-capture-modal.component';

export type TorchCaptureOutcome =
  | { readonly kind: 'captured'; readonly image: string }
  | { readonly kind: 'cancelled' }
  | { readonly kind: 'already-open' }
  | { readonly kind: 'unavailable'; readonly reason: TorchUnavailableReason };

export type PhotoSource = 'camera' | 'other' | 'already-open';

const UNAVAILABLE_REASONS: readonly unknown[] = ['no-camera', 'permission-denied', 'camera-error'];

const STATIC_DIALOG: NgbModalOptions = { animation: false, backdrop: 'static', keyboard: false };

@Injectable({ providedIn: 'root' })
export class TorchCaptureLauncher {
  private readonly modalService = inject(NgbModal);
  private modalRef: NgbModalRef | null = null;

  public async chooseSource(copy: PhotoSourceCopy, chooseFile: () => void): Promise<PhotoSource> {
    if (this.modalRef) {
      return 'already-open';
    }

    const modalRef = this.modalService.open(PhotoSourceModalComponent, {
      ...STATIC_DIALOG,
      modalDialogClass: 'bottom-modal'
    });
    modalRef.componentInstance.copy = copy;
    modalRef.componentInstance.chooseFile = chooseFile;
    this.modalRef = modalRef;

    try {
      return (await modalRef.result) === PHOTO_SOURCE_CAMERA ? 'camera' : 'other';
    } catch {
      return 'other';
    } finally {
      this.modalRef = null;
    }
  }

  public async capture(copy: TorchCaptureCopy): Promise<TorchCaptureOutcome> {
    if (this.modalRef) {
      return { kind: 'already-open' };
    }

    const modalRef = this.modalService.open(TorchCaptureModalComponent, {
      ...STATIC_DIALOG,
      modalDialogClass: 'camera-modal'
    });
    modalRef.componentInstance.copy = copy;
    this.modalRef = modalRef;

    try {
      const image: string = await modalRef.result;
      return { kind: 'captured', image };
    } catch (dismissal) {
      return this.outcomeFor(dismissal);
    } finally {
      this.modalRef = null;
    }
  }

  private outcomeFor(dismissal: unknown): TorchCaptureOutcome {
    if (UNAVAILABLE_REASONS.includes(dismissal)) {
      return { kind: 'unavailable', reason: dismissal as TorchUnavailableReason };
    }
    return { kind: 'cancelled' };
  }
}
